import React, { useState, useEffect, useCallback } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useParams,
  useLocation,
} from 'react-router-dom';
import PrimaryNavbar from './components/Navbar';
import SecondaryNavbar from './components/SecondaryNavbar';
import SliderAndCategoryView from './components/SliderAndCategoryView';
import CartPage from './components/CartPage';
import CheckoutPage from './components/CheckoutPage';
import LoginForm from './components/LoginForm';
import ProductCategoryPage from './components/ProductCategoryPage';
import ProductDetailPage from './components/ProductDetailPage';
import MyOrders from './components/MyOrders';
import FavoritesPage from './components/FavoritesPage';
import AdminDashboard from './components/AdminDashboard';
import DoctorConsultation from './components/DoctorConsultation';
import About from './components/About';
import Footer from './components/Footer';
import ContactUs from './components/ContactUs';
import { API_BASE_URL } from './config/api.config';
import { getGuestCartCount, mergeGuestDataOnLogin } from './utils/guestStore';
import './App.css';

const getStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem('user'));
  } catch {
    return null;
  }
};

const isStaff = (u) => u && (u.role === 'admin' || u.role === 'doctor');

function CategoryRoute({ searchProduct, onBackClick, onCartUpdate }) {
  const { categoryId } = useParams();
  return (
    <ProductCategoryPage
      categoryId={categoryId ? Number(categoryId) : null}
      highlightProductId={searchProduct ? searchProduct.id : null}
      onBackClick={onBackClick}
      onCartUpdate={onCartUpdate}
    />
  );
}

function AdminRoute({ user, onLogout }) {
  const navigate = useNavigate();
  const current = user || getStoredUser();

  useEffect(() => {
    if (!isStaff(current)) {
      navigate('/login', { replace: true });
    }
  }, [current, navigate]);

  if (!isStaff(current)) {
    return null;
  }

  return <AdminDashboard onLogout={onLogout} user={current} />;
}

function AppRoutes() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchProduct, setSearchProduct] = useState(null);
  const [showShippingModal, setShowShippingModal] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const [checkoutData, setCheckoutData] = useState(() => {
    try {
      const raw = sessionStorage.getItem('checkoutData');
      return raw ? JSON.parse(raw) : { cart: [], total: 0 };
    } catch {
      return { cart: [], total: 0 };
    }
  });
  const [user, setUser] = useState(getStoredUser);
  const [shippingMethod, setShippingMethod] = useState(
    () => localStorage.getItem('shippingMethod') || ''
  );

  const updateCartCount = useCallback(async () => {
    const currentUser = getStoredUser();
    if (!currentUser) {
      setCartCount(getGuestCartCount());
      return;
    }
    try {
      const response = await fetch(`${API_BASE_URL}?action=cart&userId=${currentUser.id}`);
      const data = await response.json();
      if (data.status === 'ok') {
        setCartCount(data.cart ? data.cart.length : 0);
      }
    } catch (err) {
      console.error('Error fetching cart count:', err);
    }
  }, []);

  useEffect(() => {
    updateCartCount();
    const onGuestCartUpdate = () => {
      if (!getStoredUser()) {
        setCartCount(getGuestCartCount());
      }
    };
    window.addEventListener('guest-cart-updated', onGuestCartUpdate);
    return () => window.removeEventListener('guest-cart-updated', onGuestCartUpdate);
  }, [updateCartCount]);

  // Rehydrate user from localStorage on route change (e.g. after refresh)
  useEffect(() => {
    const stored = getStoredUser();
    setUser(stored);
  }, [location.pathname]);

  const handleShippingMethodSelect = (method) => {
    setShippingMethod(method);
    if (getStoredUser()) {
      localStorage.setItem('shippingMethod', method);
    }
    setShowShippingModal(false);
  };

  const handleProductSelect = (product) => {
    setSearchProduct(product);
    const catId = product.categoryId || product.category_id;
    if (catId) {
      navigate(`/product/${product.id}?fromCategory=${catId}`);
    } else {
      navigate(`/product/${product.id}`);
    }
  };

  const handleCardClick = (id) => {
    setSearchProduct(null);
    navigate(`/category/${id}`);
  };

  const handleHomeClick = () => navigate('/');
  const handleCartClick = () => navigate('/cart');
  const handleAccountClick = () => navigate('/login');
  const handleDoctorClick = () => navigate('/doctor');
  const handleAboutClick = () => navigate('/about');
  const handleFavoritesClick = () => navigate('/favorites');
  const handleMyOrdersClick = () => navigate('/orders');

  const handleCheckoutClick = (cart, total) => {
    const data = { cart, total };
    setCheckoutData(data);
    try {
      sessionStorage.setItem('checkoutData', JSON.stringify(data));
    } catch {
      /* ignore */
    }
    navigate('/checkout');
  };

  const handleLoginSuccess = async (loggedInUser) => {
    setUser(loggedInUser);
    if (!isStaff(loggedInUser)) {
      await mergeGuestDataOnLogin(loggedInUser.id);
    }

    let redirectTo = '/orders';
    try {
      const saved = sessionStorage.getItem('redirectAfterLogin');
      if (saved && !isStaff(loggedInUser)) {
        redirectTo = saved;
        sessionStorage.removeItem('redirectAfterLogin');
      }
    } catch {
      /* ignore */
    }

    if (!isStaff(loggedInUser) && redirectTo === '/checkout') {
      try {
        const response = await fetch(`${API_BASE_URL}?action=cart&userId=${loggedInUser.id}`);
        const data = await response.json();
        if (data.status === 'ok' && data.cart?.length) {
          const cart = data.cart;
          const total = cart
            .reduce((sum, item) => sum + item.price * item.quantity, 0)
            .toFixed(2);
          const checkout = { cart, total };
          setCheckoutData(checkout);
          sessionStorage.setItem('checkoutData', JSON.stringify(checkout));
        }
      } catch {
        /* checkout page will handle empty cart */
      }
    }

    if (isStaff(loggedInUser)) {
      navigate('/admin', { replace: true });
    } else {
      navigate(redirectTo, { replace: true });
    }
    updateCartCount();
  };

  const handleLogout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('shippingMethod');
    setUser(null);
    setShippingMethod('');
    setCartCount(0);
    navigate('/', { replace: true });
  };

  const handleCheckoutSuccess = () => {
    setCartCount(0);
    setCheckoutData({ cart: [], total: 0 });
    try {
      sessionStorage.removeItem('checkoutData');
    } catch {
      /* ignore */
    }
    navigate('/orders');
  };

  const path = location.pathname;
  const isAdminPath = path === '/admin' || path.startsWith('/admin/');
  const isLoginPath = path === '/login' || path === '/account';
  const isHomePath = path === '/';
  const staffUser = user || getStoredUser();

  // Logged-in admin/doctor opening the site root (or login) → admin dashboard
  if (isStaff(staffUser) && (isHomePath || isLoginPath)) {
    return <Navigate to="/admin" replace />;
  }

  if (isAdminPath) {
    return <AdminRoute user={user} onLogout={handleLogout} />;
  }

  return (
    <div className="App">
      <PrimaryNavbar
        user={user}
        onCartClick={handleCartClick}
        onFavoritesClick={handleFavoritesClick}
        onAccountClick={handleAccountClick}
        onMyOrdersClick={handleMyOrdersClick}
        onHomeClick={handleHomeClick}
        onLogout={handleLogout}
        onShippingClick={() => setShowShippingModal(true)}
        showShippingModal={showShippingModal}
        onCloseShippingModal={() => setShowShippingModal(false)}
        onProductSelect={handleProductSelect}
        shippingMethod={shippingMethod}
        onShippingMethodSelect={handleShippingMethodSelect}
        cartCount={cartCount}
      />
      <SecondaryNavbar
        user={user}
        onAccountClick={handleAccountClick}
        onMyOrdersClick={handleMyOrdersClick}
        onDoctorClick={handleDoctorClick}
        onAboutClick={handleAboutClick}
        onFavoritesClick={handleFavoritesClick}
        onCategorySelect={handleCardClick}
        onLogout={handleLogout}
      />
      <div
        className={`content-container${isHomePath ? ' home-full' : ''}${
          isLoginPath ? ' login-full' : ''
        }`}
      >
        <Routes>
          <Route
            path="/"
            element={<SliderAndCategoryView onCardClick={handleCardClick} showImageSlider={true} />}
          />
          <Route
            path="/cart"
            element={
              <CartPage
                onBackClick={handleHomeClick}
                onCheckoutClick={handleCheckoutClick}
                onCartUpdate={updateCartCount}
              />
            }
          />
          <Route
            path="/checkout"
            element={
              <CheckoutPage
                cart={checkoutData.cart}
                total={checkoutData.total}
                shippingMethod={shippingMethod}
                onBackClick={() => navigate('/cart')}
                onSuccess={handleCheckoutSuccess}
                onLoginClick={handleAccountClick}
              />
            }
          />
          <Route
            path="/login"
            element={
              isStaff(user || getStoredUser()) ? (
                <Navigate to="/admin" replace />
              ) : (
                <LoginForm onLoginSuccess={handleLoginSuccess} />
              )
            }
          />
          <Route path="/account" element={<Navigate to="/login" replace />} />
          <Route path="/contact" element={<ContactUs onBackClick={handleHomeClick} />} />
          <Route
            path="/category/:categoryId"
            element={
              <CategoryRoute
                searchProduct={searchProduct}
                onBackClick={handleHomeClick}
                onCartUpdate={updateCartCount}
              />
            }
          />
          <Route
            path="/product/:productId"
            element={<ProductDetailPage onCartUpdate={updateCartCount} />}
          />
          <Route
            path="/orders"
            element={<MyOrders onBackClick={handleHomeClick} onLoginClick={handleAccountClick} />}
          />
          <Route
            path="/favorites"
            element={
              <FavoritesPage
                onBackClick={handleHomeClick}
                onCartUpdate={updateCartCount}
              />
            }
          />
          <Route
            path="/doctor"
            element={
              <DoctorConsultation
                onBackClick={handleHomeClick}
                user={user}
                onLoginClick={handleAccountClick}
              />
            }
          />
          <Route path="/about" element={<About onBackClick={handleHomeClick} />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
      <Footer />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppRoutes />
    </BrowserRouter>
  );
}

export default App;
